import notes from '@/sui/data/sheets/edit/notes'
import convert from '@/sui/data/sheets/convert'
import clone from 'clone'

const msg = {
  persist: `Si le problème persiste, merci de contacter un administrateur via
le formulaire de contact.`,
  verify: `merci de vérifier votre connexion internet avant de réessayer`,
  data: `Impossible de récupérer les données`,
  valid: `Impossible de contrôler les fiches sur le service`
}

function buildNotes (vm, steps, sheet) {
  const stack = [{ steps: clone(steps.fields), target: sheet }]
  while (stack.length > 0) {
    const it = stack[0]
    for (let i in it.steps) {
      const step = it.steps[i]
      if (step.initialize != null) {
        step.initialize(vm)
      }

      if (step.type === 'array') {
        let target = it.target[step.field]
        if (target != null) {
          target.push({})
        } else {
          target = [{}]
          it.target[step.field] = target
        }
        for (let i = 0; i !== target.length; ++i) {
          const subitem = target[i]
          stack.push({
            steps: step.config.fields,
            target: subitem
          })
        }
      } else if (it.target[step.field] != null &&
                 step.ignore != null &&
                 step.ignore(it.target, sheet)) {
        it.target[step.field] = null
      }
    }
    stack.shift()
  }

  return notes.buildNotes(steps, sheet, vm.surveillance)
}

function checkSheetNumbers (number, data) {
  const all = data.sheetNumbers.all
  if (all.has(number)) {
    data.sheetNumbers.duplicated.push(number)
    return
  }
  all.add(number)
}

function checkValidity (vm, steps, sheet) {
  const n = buildNotes(vm, steps, sheet)
  const result = {
    errors: 0,
    missing: 0,
    fieldErrors: {}
  }

  const stack = [{ steps: n }]
  while (stack.length > 0) {
    const it = stack[0]
    for (let i in it.steps) {
      const step = it.steps[i]
      if (Array.isArray(step)) {
        step.forEach((s) => stack.push({ steps: s }))
      } else if (step.type === 'error') {
        ++result.errors
        addOrPushToResult(
          result.fieldErrors,
          step.tooltip,
          step.referenceField.title
        )

        if (step.missing === true) {
          ++result.missing
        }
      }
    }
    stack.shift()
  }
  return result
}

function getTotal (d) {
  const dLen = d.length
  if (dLen === 1) {
    return {
      n: 'une',
      t: 'La',
      s: '',
      o: 'a'
    }
  }

  return {
    n: dLen,
    t: `Les ${dLen}`,
    s: 's',
    o: 'ont'
  }
}

function addOrPushToResult (result, key, element) {
  const f = result[key]
  if (f == null) {
    result[key] = [element]
  } else {
    f.push(element)
  }
}

function getHtmlList (l, label, txt = '') {
  if (Object.keys(l).length === 0) {
    return ''
  }

  let result = `${label}<br><ul>`
  for (let i in l) {
    const f = l[i]
    const sheetText = f.length === 1 ? 'Fiche' : 'Fiches'
    const sheetsListTxt = f.join(', ')
    result = `${result}<li>${i} ${txt} (${sheetText} N° ${sheetsListTxt})`
  }
  return `${result}</ul>`
}

function getReport (data) {
  const duplicatedSheets = data.sheetNumbers.duplicated
  let duplicatedSheetsText = ''
  if (duplicatedSheets.length !== 0) {
    duplicatedSheetsText = `
    <p style="color:red;">Certaines fiches présentent les mêmes numéros !<br>
    Veuillez vérifier les fiches N° : ${duplicatedSheets.join(', ')}</p>
    `
  }

  const missingFieldsReport = {}
  const specificErrorsFieldsReport = {}
  const errors = data.result
  for (let i = 0; i < errors.length; i++) {
    const errorSheet = errors[i]
    const errorSheetNumber = errorSheet.number
    const fieldErrors = errorSheet.fieldErrors

    for (let j in fieldErrors) {
      const errorList = fieldErrors[j]

      for (let k = 0; k !== errorList.length; k++) {
        if (j === 'Champ requis pour la validation de la fiche') {
          addOrPushToResult(missingFieldsReport, errorList[k], errorSheetNumber)
          continue
        }
        addOrPushToResult(specificErrorsFieldsReport, j, errorSheetNumber)
      }
    }
  }

  const sheetsTotal = getTotal(data.serverSheets)
  const errorsTotal = getTotal(errors)
  return `
    ${sheetsTotal.t} fiche${sheetsTotal.s} saisie${sheetsTotal.s} ${sheetsTotal.o}
    bien été importée${sheetsTotal.s} dans votre espace personnel.<br>
    Il subsiste cependant ${errorsTotal.n} fiche${errorsTotal.s} présentant
    des informations manquantes ou à corriger.<br><br>
    ${duplicatedSheetsText}
    ${getHtmlList(
    missingFieldsReport,
    'Les champs manquants ci-dessous sont à compléter :',
    'manquant'
  )}
    ${getHtmlList(
    specificErrorsFieldsReport,
    'Les erreurs ci-dessous sont à corriger :'
  )}
   `
}

export default {
  steps: {
    validate: function (getList, askConfirmation) {
      return {
        title: 'Vérification des champs',
        continue: (data) => data.valid.length > 0,
        progress: {
          value: 0
        },

        run: function (vm, data, iteration) {
          const list = getList(vm, data, iteration)
          if (iteration === 0) {
            data.result = []
            data.valid = []
            data.serverSheets = []
            data.sheetNumbers = {
              all: new Set(),
              duplicated: []
            }
          }
          const step = 10
          const max = list.length
          const start = iteration * step
          if (start >= max) {
            const error = this.buildError(data)
            return {
              end: true,
              success: data.result.length === 0,
              askConfirmation: askConfirmation,
              report: askConfirmation ? getReport(data) : null,
              error: error
            }
          }

          const end = start + step > max ? max : start + step
          this.progress.value = Math.round(100 * end / max)
          for (let i = start; i < end; ++i) {
            const listSheet = list[i]
            const sheet = convert.serverToClient(listSheet)
            data.serverSheets.push(sheet)

            vm.sheet = sheet.sheet
            const result = checkValidity(vm, vm.reference, sheet.sheet)
            vm.sheet = null
            if (result.errors !== 0 || result.missing !== 0) {
              const number = sheet.sheet.sheetNumber
              result.number = number != null && number !== ''
                ? parseInt(number, 10) : -1
              checkSheetNumbers(result.number, data)
              result.id = listSheet.id
              result.v = listSheet.v
              data.result.push(result)
            } else {
              data.valid.push(listSheet)
            }
          }

          return { end: false }
        },

        buildError: function (data) {
          const reducer = function (result, r) {
            result.push(r.number)
            return result
          }
          const sheets = data.result.reduce(reducer, [])
          sheets.sort((a, b) => a - b)

          return `Merci de corriger les champs avec des erreurs sur les fiches avec les numéros
suivants : ${sheets.map(e => e === -1 ? '(non renseigné)'
    : e.toString()).join(', ')}.`
        },

        setError: function () {
          this.error = `Erreur interne lors de la vérification des champs.
${msg.persist}`
        }
      }
    },

    statuses: function (module) {
      return {
        title: 'Mise à jour du statut des fiches',
        progress: {
          value: 0
        },

        setTarget: function (target, status, step, iteration) {
          this.total = Math.ceil(target.length / step)
          this.target = target
          this.status = status
          this.start = iteration
          this.ended = 0
        },

        run: function (vm, data, iteration) {
          const step = 100
          if (iteration === 0) {
            const reducer = (result, f) => {
              result.push({ id: f.id, ver: f.v })
              return result
            }
            this.valid = []
            this.open = []
            this.errors = []
            data.valid.reduce(reducer, this.valid)
            data.result.reduce(reducer, this.open)
            this.setTarget(this.valid, 'valid', step, 0)
          }
          let max = this.target.length
          let start = (iteration - this.start) * step
          if (start >= max) {
            const end = this.ended === this.total
            if (end) {
              if (this.status === 'valid') {
                this.setTarget(this.open, 'open', step, iteration + 1)
                return {
                  end: false
                }
              } else {
                return {
                  success: this.errors.length === 0,
                  error: this.buildError(),
                  msg: `
${this.valid.length} fiche.s contrôlée.s avec succès.<br />
${this.open.length} fiche.s contien.nen.t des erreurs.
`,
                  end: end
                }
              }
            } else {
              return {
                end: false
              }
            }
          }

          const end = start + step > max ? max : start + step
          this.progress.value = Math.round(100 * end / max)
          const instance = this
          vm.$store.dispatch(
            'sheet/statuses',
            {
              m: module(vm),
              st: this.status,
              sh: this.target.slice(start, end),
              sv: vm.surveillance.id,
              y: vm.surveillance.year
            }
          ).then(
            (response) => {
              if (response.data.err != null) {
                instance.errors.push(response.data.err)
              }
              instance.ended++
              return response
            }
          ).catch(
            (err) => {
              instance.ended++
              instance.errors.push(err)
            }
          )

          return { end: false }
        },

        buildError: function () {
          return this.errors.length === this.total
            ? `Aucune fiche n'a pu être contrôlée sur le serveur, ${msg.verify}.
${msg.persist}`
            : `Certaines fiches n'ont pas pu être contrôlées sur le serveur,
${msg.verify}. ${msg.persist}`
        },

        setError: function () {
          this.error = `${msg.valid}, ${msg.verify}. ${msg.persist}`
        }
      }
    }
  },

  msg: msg,
  checkValidity: checkValidity
}
